import { useCallback, useState } from "react";

import toast from "react-hot-toast";
import { useTranslation } from "react-i18next";

import { styled } from "simplydo/react/styled";
import { Button } from "simplydo/react/ui/button";
import { Input } from "simplydo/react/ui/input";
import {
  Modal,
  ModalActions,
  ModalContent,
  ModalHeader,
} from "simplydo/react/ui/modal";

import api from "api";

const InputContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  margin-bottom: 1rem;
  min-width: 350px;
  > span {
    display: block;
    margin-bottom: 5px;
    font-weight: 600;
  }
  .ui.input {
    min-width: 350px;
  }
`;

type GatedChallengeModalProps = {
  onCloseModal: () => void;
  openModal: boolean;
  challengeName: string;
  challengeId: string;
  newGroupUsers: string[];
};

export const GatedChallengeModal = ({
  onCloseModal,
  openModal,
  challengeName,
  challengeId,
  newGroupUsers,
}: GatedChallengeModalProps) => {
  const { t } = useTranslation();
  const [nextPhaseChallengeName, setNextPhaseChallengeName] = useState(
    `Next-phase: ${challengeName}`,
  );
  const [nextPhaseGroupName, setNextPhaseGroupName] = useState(
    `Next-phase ${t("generic.group")}: ${challengeName}`,
  );
  const [isLoading, setIsLoading] = useState(false);

  const createNextPhase = useCallback(() => {
    setIsLoading(true);
    const data = {
      challengeName: nextPhaseChallengeName,
      groupName: nextPhaseGroupName,
      newGroupUsers: newGroupUsers,
    };
    api.authenticatedRequest(
      "POST",
      `/challenges/${challengeId}/gatedChallenge/create`,
      data,
      () => {
        setIsLoading(false);
        toast.success(
          `Next-phase ${t("generic.challenge")}: ${nextPhaseChallengeName}, and ${t("generic.group")}: ${nextPhaseGroupName} created.`,
        );
        onCloseModal();
      },
      (err) => {
        setIsLoading(false);
        toast.error(err.message);
      },
    );
  }, [
    challengeId,
    nextPhaseChallengeName,
    nextPhaseGroupName,
    newGroupUsers,
    onCloseModal,
    t,
  ]);

  return (
    <Modal onClose={onCloseModal} open={openModal}>
      <ModalHeader>
        Create next-phase (gated) {t("generic.challenge")} based on{" "}
        {challengeName}
      </ModalHeader>
      <ModalContent scrolling>
        <p>{t("groups.gatedChallenge.body")}</p>
        <p>{t("groups.gatedChallenge.challengeAndGroup")}</p>
        <InputContainer>
          <span>New {t("generic.challenge")} name:</span>
          <Input
            defaultValue={nextPhaseChallengeName}
            onChange={(e, { value }) => {
              setNextPhaseChallengeName(value);
            }}
          ></Input>
        </InputContainer>

        <InputContainer>
          <span>{t("groups.gatedChallenge.newGroupName")}</span>
          <Input
            defaultValue={nextPhaseGroupName}
            onChange={(e, { value }) => {
              setNextPhaseGroupName(value);
            }}
          ></Input>
        </InputContainer>
      </ModalContent>
      <ModalActions>
        <Button onClick={onCloseModal}>{"Cancel"}</Button>

        <Button
          disabled={!nextPhaseChallengeName || !nextPhaseGroupName}
          loading={isLoading}
          onClick={createNextPhase}
          color="primary"
        >
          {"Create next phase"}
        </Button>
      </ModalActions>
    </Modal>
  );
};
